/**
 * Helpers for reading an RTK Query mutation result.
 *
 * `axiosBaseQuery` returns `error` in two shapes:
 *  - a business error from a 2xx body → `{ error: string[] }`
 *  - an HTTP failure (4xx/5xx)        → `{ error: <response body> }`, an object
 *
 * Call sites that test `result?.error?.length` only catch the first, so a 500
 * falls through to the success branch and the user is told the action worked.
 */

interface MutationResult {
  error?: unknown;
  data?: unknown;
}

/** True when the mutation failed, in either error shape. */
export const isMutationError = (
  result: MutationResult | undefined
): boolean => {
  const error = result?.error;
  if (!error) return false;
  if (Array.isArray(error)) return error.length > 0;
  if (typeof error === 'object') return Object.keys(error).length > 0;
  return true;
};

/**
 * The first business-error string, when there is one. HTTP failures carry a
 * response body rather than messages, so they return undefined and the caller
 * should fall back to its own copy.
 */
export const firstMutationError = (
  result: MutationResult | undefined
): string | undefined => {
  const error = result?.error;
  if (Array.isArray(error) && typeof error[0] === 'string') return error[0];
  return undefined;
};
